/**
 * Input dispatch — keyboard, mouse, edge scroll, click handlers.
 *
 * v0M2.4 wires camera pan (arrows / WASD / edge scroll) and camera zoom
 * (wheel + `+` / `-`).
 * v0M3.10 adds left-click + right-click callbacks + Escape handling per
 * MECHANICS.md §5 rules 1 (left-select / right-order) and 3 (hover-
 * before-click certainty). Callbacks receive canvas-relative screen
 * coordinates; consumers translate to world via screenToWorld.
 */

import type { Camera } from './map/camera';
import { panCamera, zoomIn, zoomInAt, zoomOut, zoomOutAt } from './map/camera';
// v0M15.4b (2026-07-30) — keybindings read at mount time and refreshed
// via subscribeSettings when the Settings modal mutates a binding.
import { loadSettings, subscribeSettings } from './settings-storage';

/** Pan speed in cells / second when a key is held. */
const KEYBOARD_PAN_CELLS_PER_SECOND = 12;

/** Pan speed for edge scroll (cells / second when cursor is at the very edge). */
const EDGE_PAN_CELLS_PER_SECOND = 10;

/** MFix1.1 (2026-07-16): Shift-held pan-speed multiplier per Michael's
 *  playtest note — "player needs to be able to pan the map quicker
 *  (perhaps by holding the shift key)". Applies uniformly to keyboard +
 *  edge scroll. */
const FAST_PAN_MULTIPLIER = 3;

/** Edge scroll trigger zone (px from canvas edge). */
const EDGE_SCROLL_ZONE_PX = 24;

interface HeldKeys {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

interface HeldModifiers {
  /**
   * MFix1.1 / v0M15.4b: true while the rebindable fast-pan key is
   * depressed — applies the FAST_PAN_MULTIPLIER to both keyboard and
   * edge-scroll pan speeds. Default binding is `Shift` (either
   * ShiftLeft or ShiftRight); user-rebindable to any single `KeyboardEvent.code`.
   *
   * Retains the `shift` field name for source-history stability — the
   * semantic is "fast-pan modifier held", not literally the Shift key.
   */
  shift: boolean;
}

interface MouseState {
  x: number;
  y: number;
  overCanvas: boolean;
}

export interface InputState {
  readonly held: HeldKeys;
  readonly modifiers: HeldModifiers;
  readonly mouse: MouseState;
}

/**
 * Consumer-supplied callbacks fired on mouse click / Escape (v0M3.10).
 * Coordinates are canvas-relative screen pixels; callers translate to
 * world via `screenToWorld` or `screenToCell`.
 */
export interface InputCallbacks {
  onLeftClick?: (sx: number, sy: number) => void;
  // MFix12.4 (2026-07-27) — shift flag added for the shift+RMB waypoint
  // chaining feature. Pre-existing consumers ignore the third arg.
  onRightClick?: (sx: number, sy: number, shift: boolean) => void;
  onEscape?: () => void;
  /**
   * v0M7.5b (2026-07-26) — Enter key. Currently used to commit an
   * accumulating Road polyline; may serve additional confirm actions
   * later.
   */
  onEnter?: () => void;
}

/**
 * Wire input events on the given canvas + window. Returns the InputState
 * that the renderer polls each frame, plus a cleanup function.
 */
export function mountInput(
  canvas: HTMLCanvasElement,
  camera: Camera,
  callbacks: InputCallbacks = {},
): { input: InputState; cleanup: () => void } {
  const held: HeldKeys = { up: false, down: false, left: false, right: false };
  const modifiers: HeldModifiers = { shift: false };
  const mouse: MouseState = { x: 0, y: 0, overCanvas: false };

  // v0M15.4b (2026-07-30) — cache the currently-bound key codes so the
  // per-event handlers don't re-read localStorage on every keystroke.
  // Refreshed by the subscribeSettings callback below whenever the
  // user changes a binding in the Settings modal.
  let bindFastPan = loadSettings().keybindings.fastPan.code;
  let bindOpenMenu = loadSettings().keybindings.openMenu.code;
  const unsubKeybindings = subscribeSettings((s) => {
    bindFastPan = s.keybindings.fastPan.code;
    bindOpenMenu = s.keybindings.openMenu.code;
  });

  /**
   * v0M15.4b — does `e.code` (or the special-case shift agnostic path)
   * match `binding`? The default `Shift` binding treats either
   * `ShiftLeft` or `ShiftRight` as a match; any concrete `Shift*` code
   * binds to that specific side only.
   */
  const codeMatchesBinding = (e: KeyboardEvent, binding: string): boolean => {
    if (binding === 'Shift') return e.code === 'ShiftLeft' || e.code === 'ShiftRight' || e.shiftKey;
    return e.code === binding;
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    // v0M15.4b (2026-07-30) — fast-pan modifier is set by the
    // currently-bound key (default: either Shift). Previously
    // hardcoded to e.shiftKey; now driven through settings.
    if (codeMatchesBinding(e, bindFastPan)) modifiers.shift = true;
    switch (e.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        held.up = true;
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        held.down = true;
        break;
      case 'ArrowLeft':
      case 'a':
      case 'A':
        held.left = true;
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        held.right = true;
        break;
      case '+':
      case '=':
        zoomIn(camera);
        break;
      case '-':
      case '_':
        zoomOut(camera);
        break;
    }
  };

  const onKeyUp = (e: KeyboardEvent): void => {
    // v0M15.4b — clear fast-pan modifier when the bound key releases.
    if (codeMatchesBinding(e, bindFastPan)) modifiers.shift = false;
    switch (e.key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        held.up = false;
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        held.down = false;
        break;
      case 'ArrowLeft':
      case 'a':
      case 'A':
        held.left = false;
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        held.right = false;
        break;
    }
  };

  const onMouseMove = (e: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = e.clientX - rect.left;
    mouse.y = e.clientY - rect.top;
    mouse.overCanvas = true;
  };

  const onMouseLeave = (): void => {
    mouse.overCanvas = false;
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    // MFix1.2 (2026-07-16) — zoom targets the pixel under the cursor,
    // not the screen centre. Falls back to the plain zoom (which uses
    // centre-anchor) if the cursor position isn't on the canvas.
    const rect = canvas.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    if (e.deltaY < 0) {
      zoomInAt(camera, cursorX, cursorY, canvas.clientWidth, canvas.clientHeight);
    } else if (e.deltaY > 0) {
      zoomOutAt(camera, cursorX, cursorY, canvas.clientWidth, canvas.clientHeight);
    }
  };

  // v0M3.10 — left-click / right-click / Escape.
  const onMouseDown = (e: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    if (e.button === 0) callbacks.onLeftClick?.(sx, sy);
    else if (e.button === 2) {
      e.preventDefault();
      callbacks.onRightClick?.(sx, sy, e.shiftKey);
    }
  };

  const onContextMenu = (e: MouseEvent): void => {
    // Suppress the browser context menu on the canvas so right-click
    // is available as an order button.
    e.preventDefault();
  };

  const onEscape = (e: KeyboardEvent): void => {
    // v0M15.4b — the "open menu / clear pending action" key is rebindable.
    // Default binding is `Escape` (e.code === 'Escape'); users can rebind
    // to any single-key code. The consumer callback name stays `onEscape`
    // for source-history stability.
    if (codeMatchesBinding(e, bindOpenMenu)) callbacks.onEscape?.();
  };

  // v0M7.5b (2026-07-26) — separate listener so consumers that
  // don't set onEnter pay nothing.
  const onEnter = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') callbacks.onEnter?.();
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('keydown', onEscape);
  window.addEventListener('keydown', onEnter);
  canvas.addEventListener('mousemove', onMouseMove);
  canvas.addEventListener('mouseleave', onMouseLeave);
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  const cleanup = (): void => {
    unsubKeybindings();
    window.removeEventListener('keydown', onEnter);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('keydown', onEscape);
    canvas.removeEventListener('mousemove', onMouseMove);
    canvas.removeEventListener('mouseleave', onMouseLeave);
    canvas.removeEventListener('mousedown', onMouseDown);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('wheel', onWheel);
  };

  return { input: { held, modifiers, mouse }, cleanup };
}

/**
 * Advance the camera by the input's held keys + edge-scroll state for
 * the given frame delta. Called each frame by the renderer.
 *
 * MFix1.1: holding Shift multiplies both keyboard and edge-scroll pan
 * speeds by FAST_PAN_MULTIPLIER.
 */
export function applyPanFromInput(
  camera: Camera,
  input: InputState,
  canvas: HTMLCanvasElement,
  dtSeconds: number,
): void {
  const boost = input.modifiers.shift ? FAST_PAN_MULTIPLIER : 1;
  const keyStep = KEYBOARD_PAN_CELLS_PER_SECOND * dtSeconds * boost;
  let dx = 0;
  let dy = 0;
  if (input.held.left) dx -= keyStep;
  if (input.held.right) dx += keyStep;
  if (input.held.up) dy -= keyStep;
  if (input.held.down) dy += keyStep;

  if (input.mouse.overCanvas) {
    const edgeStep = EDGE_PAN_CELLS_PER_SECOND * dtSeconds * boost;
    if (input.mouse.x < EDGE_SCROLL_ZONE_PX) dx -= edgeStep;
    if (input.mouse.x > canvas.clientWidth - EDGE_SCROLL_ZONE_PX) dx += edgeStep;
    if (input.mouse.y < EDGE_SCROLL_ZONE_PX) dy -= edgeStep;
    if (input.mouse.y > canvas.clientHeight - EDGE_SCROLL_ZONE_PX) dy += edgeStep;
  }

  if (dx !== 0 || dy !== 0) {
    panCamera(camera, dx, dy);
  }
}
